<script setup lang="ts">
import { Input, ZhButton } from '@zerohuman/ui'
import { computed, ref, useId } from 'vue'
import { api, type NamesTemplate } from '@/api'
import type { NamesAnswers } from '@/lib/setupChat'

/**
 * Setup's names step (#2216), the same form under the setup conversation and on the guided Names screen: the team's
 * name, filled in with the template's, and one name per teammate, each blank until given. A blank teammate goes by
 * its role titles (#1740), which its field shows as the placeholder, so nothing is ever named by default. "Pick names
 * for me" asks the server for one name per blank field and fills the blanks alone, as the Catalog's clone panel
 * does: what was typed is never overwritten, and every filled field stays editable.
 *
 * The form saves nothing itself. Continue emits `save` with the trimmed answers (a blank teammate stays `''`, for the
 * role titles); "Skip, keep role titles" emits `skip`, which the parent saves as `{}` so the template's name and the
 * role titles apply. A `failure`, the server's plain-words refusal of the last save (the name is taken, no room for
 * the teammates…), is shown against the form, which keeps what was typed so it can be changed and saved again.
 */
const props = withDefaults(
    defineProps<{
        template: NamesTemplate
        /** A save is on its way: every control waits on it. */
        busy?: boolean
        /** Why the last save was refused, in plain words, or null. */
        failure?: string | null
    }>(),
    { busy: false, failure: null },
)

const emit = defineEmits<{
    save: [answers: NamesAnswers]
    skip: []
}>()

const id = useId()
const teamName = ref(props.template.name)
const teammateNames = ref<string[]>(props.template.teammates.map(() => ''))
const picking = ref(false)
const pickFailure = ref<string | null>(null)

/** The fields "Pick names for me" would fill: the blank teammates, in order. */
const blankTeammates = computed(() =>
    teammateNames.value.flatMap((teammateName, index) => (teammateName.trim() ? [] : [index])),
)

/** The most names one request may ask for: the API's `MAX_NAME_SUGGESTIONS`. */
const MAX_NAME_SUGGESTIONS = 50

/** What an unnamed teammate goes by: its role titles, as the assembly names it. */
function placeholderOf(slot: { roles: string[] }, index: number): string {
    return slot.roles.join(' / ') || `Teammate ${index + 1}`
}

/** How a name is compared with one already in the form: whole and by its first word, ignoring case. */
function nameKeys(value: string): string[] {
    const whole = value.trim().toLowerCase()
    return whole ? [whole, whole.split(/\s+/)[0] ?? whole] : []
}

/**
 * `names` less any the form already holds, each once, in the server's order: two teammates given one name would
 * clash on their mail identity and the assembly would refuse them.
 */
function unusedNames(names: readonly string[]): string[] {
    const taken = new Set(teammateNames.value.filter(given => given.trim()).flatMap(nameKeys))
    return names.filter(candidate => {
        const keys = nameKeys(candidate)
        if (keys.length === 0 || keys.some(key => taken.has(key))) {
            return false
        }
        for (const key of keys) {
            taken.add(key)
        }
        return true
    })
}

/**
 * "Pick names for me": one name per blank teammate, filled in order. The server's order is fixed per person, so when
 * its answer holds names the form already has, it is asked once more for enough to make up for every typed field.
 */
async function pickNames(): Promise<void> {
    const count = blankTeammates.value.length
    if (count === 0 || picking.value || props.busy) {
        return
    }
    picking.value = true
    pickFailure.value = null
    try {
        const answer = (await api.suggestMemberNames(count))?.names ?? []
        let names = unusedNames(answer)
        if (names.length < count && names.length < answer.length) {
            const typed = teammateNames.value.length - count
            const more = (await api.suggestMemberNames(Math.min(count + typed, MAX_NAME_SUGGESTIONS)))?.names ?? []
            names = unusedNames([...answer, ...more])
        }
        const filled = [...teammateNames.value]
        for (const index of blankTeammates.value) {
            const next = names.shift()
            if (!next) {
                break
            }
            filled[index] = next
        }
        teammateNames.value = filled
    } catch {
        pickFailure.value = 'Could not pick names just now. Type them, or try again.'
    } finally {
        picking.value = false
    }
}

function save(): void {
    if (props.busy) {
        return
    }
    emit('save', {
        teamName: teamName.value.trim(),
        teammateNames: teammateNames.value.map(teammateName => teammateName.trim()),
    })
}

function skip(): void {
    if (!props.busy) {
        emit('skip')
    }
}
</script>

<template>
    <form class="space-y-4" data-testid="setup-names" @submit.prevent="save">
        <div class="space-y-2">
            <label class="block text-sm" :for="`setup-team-name-${id}`">Team name</label>
            <Input
                :id="`setup-team-name-${id}`"
                v-model="teamName"
                type="text"
                data-testid="setup-team-name"
                :disabled="busy"
            />
        </div>
        <p class="text-sm">Name your teammates, or leave one blank to call it by its role.</p>
        <div v-for="(slot, index) in template.teammates" :key="index" class="space-y-2">
            <label class="block text-sm" :for="`setup-teammate-${id}-${index}`">Teammate {{ index + 1 }} name</label>
            <Input
                :id="`setup-teammate-${id}-${index}`"
                v-model="teammateNames[index]"
                type="text"
                :placeholder="placeholderOf(slot, index)"
                data-testid="setup-teammate-name"
                :disabled="busy"
            />
        </div>
        <p v-if="failure" role="alert" class="text-sm" data-testid="setup-names-failure">{{ failure }}</p>
        <p v-if="pickFailure" role="status" class="text-sm" data-testid="setup-names-pick-failure">{{ pickFailure }}</p>
        <div class="flex flex-wrap items-center gap-3">
            <slot name="actions" />
            <ZhButton
                v-if="template.teammates.length > 0"
                variant="ghost"
                :disabled="busy || blankTeammates.length === 0"
                :loading="picking"
                loading-label="PICKING..."
                data-testid="setup-names-pick"
                @click="pickNames"
            >
                Pick names for me
            </ZhButton>
            <ZhButton variant="ghost" :disabled="busy" data-testid="setup-names-skip" @click="skip">
                Skip, keep role titles
            </ZhButton>
            <ZhButton :loading="busy" loading-label="SAVING..." data-testid="setup-names-save" @click="save">
                Continue
            </ZhButton>
        </div>
    </form>
</template>
